import { LegalPageLayout, Section, Bullet } from "@/components/legal/LegalPageLayout";

const LAST_UPDATED = "18 March 2026";

export function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      crossLink={{ to: "/terms", label: "Terms of Service" }}
      contactEmail="privacy@chargesuite.io"
    >
      <p className="mt-6 text-sm leading-relaxed text-[var(--color-text-secondary)]">
        ChargeSuite GmbH (&ldquo;ChargeSuite&rdquo;, &ldquo;we&rdquo;,
        &ldquo;us&rdquo;, or &ldquo;our&rdquo;) operates an AI-powered
        regulatory compliance and grid revenue optimisation platform for EV
        charging operators in Germany and the European Union. This Privacy
        Policy explains how we collect, use, store, and share personal data
        in connection with our services. ChargeSuite is a business-to-business
        (&ldquo;B2B&rdquo;) platform; our customers are legal entities
        (EV charging operators). We do not offer services directly to
        consumers.
      </p>

      {/* 1. Data Controller */}
      <Section id="section-controller" title="1. Data Controller">
        <p>
          The data controller for processing activities described in this
          policy is:
        </p>
        <address className="not-italic rounded-lg border border-[var(--color-border)] bg-gray-50 px-4 py-3 text-sm leading-6 text-[var(--color-text-primary)]">
          ChargeSuite GmbH<br />
          [Registered address — to be confirmed upon incorporation]<br />
          Frankfurt am Main, Germany<br />
          E-mail:{" "}
          <a
            href="mailto:privacy@chargesuite.io"
            className="text-[var(--color-primary)] hover:underline"
          >
            privacy@chargesuite.io
          </a>
        </address>
        <p>
          Data Protection Officer (DPO): [DPO name and contact details — to
          be appointed. For inquiries in the interim, contact{" "}
          <a
            href="mailto:privacy@chargesuite.io"
            className="text-[var(--color-primary)] hover:underline"
          >
            privacy@chargesuite.io
          </a>
          ].
        </p>
      </Section>

      {/* 2. Data We Collect */}
      <Section id="section-data-collected" title="2. Data We Collect">
        <p>
          We process the following categories of data when you use
          ChargeSuite:
        </p>
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Category
                </th>
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Examples
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {[
                {
                  category: "Operator account data",
                  examples:
                    "Company name, registered address, VAT number, contact name, business e-mail, country of operation",
                },
                {
                  category: "Authentication data",
                  examples:
                    "E-mail address, hashed credentials, multi-factor authentication tokens (processed by Clerk)",
                },
                {
                  category: "Compliance profile data",
                  examples:
                    "Charger inventory, certification status (Eichrecht, ISO 15118), payment method support, CPMS provider",
                },
                {
                  category: "Compliance Q&A logs",
                  examples:
                    "Questions submitted to the Compliance Brain AI, AI-generated answers, source citations, confidence scores, user feedback ratings",
                },
                {
                  category: "Charging session metadata",
                  examples:
                    "Session timestamps, energy delivered (kWh), tariff applied, V2G dispatch events — no individual driver identifiers are processed",
                },
                {
                  category: "Usage analytics",
                  examples:
                    "Pages visited, feature usage frequency, error events, API call latency — used to improve platform reliability",
                },
                {
                  category: "Billing data",
                  examples:
                    "Subscription tier, invoice history (Stripe processes card details; we do not store raw card numbers)",
                },
              ].map((row) => (
                <tr key={row.category} className="align-top">
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    {row.category}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {row.examples}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 3. Purposes and Legal Basis */}
      <Section id="section-purposes" title="3. Purposes and Legal Basis">
        <p>
          We process personal data only where we have a lawful basis under
          GDPR Art. 6:
        </p>
        <ul className="space-y-3">
          {[
            {
              basis: "Art. 6(1)(b) — Contract performance",
              purpose:
                "Providing the ChargeSuite platform: user authentication, compliance analysis, gap reports, regulatory alerts, V2G optimisation, subsidy matching, and billing management.",
            },
            {
              basis: "Art. 6(1)(c) — Legal obligation",
              purpose:
                "Compliance with applicable law, including responding to lawful requests from German supervisory authorities.",
            },
            {
              basis: "Art. 6(1)(f) — Legitimate interests",
              purpose:
                "Platform security and fraud prevention, service reliability monitoring, product improvement analytics, and enforcing our Terms of Service. Our legitimate interests do not override your rights as a data subject.",
            },
            {
              basis: "Art. 6(1)(a) — Consent",
              purpose:
                "Where we send optional marketing communications to your business contact. You may withdraw consent at any time.",
            },
          ].map((item) => (
            <li
              key={item.basis}
              className="rounded-lg border border-[var(--color-border)] px-4 py-3"
            >
              <p className="font-semibold text-[var(--color-text-primary)]">
                {item.basis}
              </p>
              <p className="mt-1">{item.purpose}</p>
            </li>
          ))}
        </ul>
      </Section>

      {/* 4. Retention */}
      <Section id="section-retention" title="4. Retention Periods">
        <p>
          We retain personal data for as long as necessary to fulfil the
          purposes set out above, subject to the following schedules:
        </p>
        <ul className="space-y-2">
          {[
            "Account and compliance profile data: retained for the duration of the active contract, then deleted or anonymised within 30 days of contract termination.",
            "Compliance Q&A logs and gap reports: retained for 3 years after account deletion to support regulatory audit trails, then permanently deleted.",
            "Charging session metadata: retained for 3 years to meet German energy-law record-keeping requirements (EnWG).",
            "Usage analytics (aggregated): retained indefinitely in anonymised form.",
            "Billing records: retained for 10 years in accordance with German commercial law (HGB § 257).",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
        <p>
          Upon a verified erasure request under GDPR Art. 17, we will
          delete all personal data within 30 days, except where retention
          is required by law.
        </p>
      </Section>

      {/* 5. Sub-processors */}
      <Section id="section-subprocessors" title="5. Sub-processors">
        <p>
          We engage the following sub-processors to deliver our services.
          All sub-processors are bound by Data Processing Agreements that
          require equivalent GDPR protections:
        </p>
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Sub-processor
                </th>
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Purpose
                </th>
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Location
                </th>
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Transfer mechanism
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {[
                {
                  name: "Amazon Web Services (AWS)",
                  purpose:
                    "Cloud infrastructure: compute (ECS Fargate), relational database (RDS PostgreSQL + pgvector), cache (ElastiCache Redis)",
                  location: "eu-central-1 (Frankfurt, Germany)",
                  transfer: "Within EU — no transfer mechanism required",
                },
                {
                  name: "Clerk, Inc.",
                  purpose:
                    "User authentication and organisation management",
                  location: "United States",
                  transfer: "EU Standard Contractual Clauses (SCCs)",
                },
                {
                  name: "Anthropic, PBC",
                  purpose:
                    "AI inference for the Compliance Brain (Claude API)",
                  location: "United States",
                  transfer: "EU Standard Contractual Clauses (SCCs)",
                },
                {
                  name: "Stripe, Inc.",
                  purpose: "Subscription billing and payment processing",
                  location: "United States / Ireland",
                  transfer:
                    "EU Standard Contractual Clauses (SCCs) / Within EU",
                },
                {
                  name: "Sentry (Functional Software, Inc.)",
                  purpose: "Application error monitoring",
                  location: "United States",
                  transfer: "EU Standard Contractual Clauses (SCCs)",
                },
              ].map((row) => (
                <tr key={row.name} className="align-top">
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    {row.name}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {row.purpose}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {row.location}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {row.transfer}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          We will notify you at least 30 days in advance of any material
          changes to our sub-processor list.
        </p>
      </Section>

      {/* 6. Auftragsverarbeitung (AVV) */}
      <Section id="section-avv" title="6. Auftragsverarbeitung (AVV) — Unternehmenskunden">
        <p>
          Für Unternehmenskunden, die personenbezogene Daten im Rahmen der ChargeSuite-Dienste
          verarbeiten lassen, stellen wir einen{" "}
          <strong>Auftragsverarbeitungsvertrag (AVV)</strong> gemäß Art. 28 DSGVO zur Verfügung.
          Der AVV regelt Gegenstand, Dauer, Art und Zweck der Verarbeitung sowie technische und
          organisatorische Maßnahmen (TOMs).
        </p>
        <p>
          Der AVV wird vor Vertragsabschluss auf Anfrage bereitgestellt — insbesondere für
          Stadtwerke und öffentlich-rechtliche CPOs, die im Rahmen ihrer Ausschreibungsverfahren
          eine unterzeichnete AVV benötigen.
        </p>
        <p>
          <strong>Datenverarbeitung ausschließlich in Deutschland.</strong> Alle Kundendaten —
          einschließlich KI-Compliance-Anfragen, Betreiberprofile und Lückenanalyse-Ergebnisse —
          werden auf Servern in Deutschland (AWS eu-central-1, Frankfurt) verarbeitet und gespeichert.
        </p>
        <p>
          Anfragen bitte an:{" "}
          <a href="mailto:datenschutz@chargesuite.de">datenschutz@chargesuite.de</a>
        </p>
      </Section>

      {/* 7. Data Subject Rights */}
      <Section id="section-rights" title="7. Your Data Subject Rights">
        <p>
          As a data subject (or as the representative of your organisation),
          you have the following rights under GDPR:
        </p>
        <ul className="space-y-2">
          {[
            {
              right: "Right of access (Art. 15)",
              desc: "Request a copy of personal data we hold about you.",
            },
            {
              right: "Right to rectification (Art. 16)",
              desc: "Request correction of inaccurate personal data.",
            },
            {
              right: "Right to erasure (Art. 17)",
              desc: "Request deletion of your personal data where no legal obligation to retain exists. In-app erasure is available via Settings.",
            },
            {
              right: "Right to data portability (Art. 20)",
              desc: "Receive your data in a structured, machine-readable format (JSON/CSV export available on request).",
            },
            {
              right: "Right to restriction (Art. 18)",
              desc: "Request that we limit processing while a dispute is resolved.",
            },
            {
              right: "Right to object (Art. 21)",
              desc: "Object to processing based on legitimate interests or for direct marketing.",
            },
            {
              right: "Right to lodge a complaint",
              desc: "You may lodge a complaint with the Hessian Commissioner for Data Protection and Freedom of Information (HBDI) or any EU supervisory authority.",
            },
          ].map((item) => (
            <Bullet key={item.right}>
              <strong className="font-medium text-[var(--color-text-primary)]">
                {item.right}:
              </strong>{" "}
              {item.desc}
            </Bullet>
          ))}
        </ul>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a
            href="mailto:privacy@chargesuite.io"
            className="text-[var(--color-primary)] hover:underline"
          >
            privacy@chargesuite.io
          </a>
          . We will respond within 30 days.
        </p>
      </Section>

      {/* 7. Security */}
      <Section id="section-security" title="7. Security Measures">
        <p>
          We implement appropriate technical and organisational measures to
          protect personal data, including:
        </p>
        <ul className="space-y-2">
          {[
            "Encryption at rest using AES-256 on all AWS RDS and S3 resources",
            "Encryption in transit using TLS 1.2 or higher for all API and browser connections",
            "Row-Level Security (RLS) in PostgreSQL enforcing tenant data isolation",
            "Audit logging for all data access and administrative actions",
            "Access controls with least-privilege principles; production access restricted to authorised personnel",
            "Regular vulnerability scanning and dependency audits (npm audit, Dependabot)",
            "Breach notification to affected operators and supervisory authorities within 72 hours of becoming aware of a breach",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
      </Section>

      {/* 8. AI Processing */}
      <Section id="section-ai" title="8. AI Processing Disclosure">
        <p>
          ChargeSuite uses the Anthropic Claude API to generate regulatory
          compliance answers. When you submit a query to the Compliance
          Brain, the following data is transmitted to Anthropic&apos;s API:
        </p>
        <ul className="space-y-2">
          {[
            "The text of your compliance question",
            "Relevant excerpts from the regulatory corpus (no personal data)",
            "Your operator profile summary (company type, charger count, certifications — no individual employee names)",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
        <p>
          Anthropic processes this data under a Data Processing Agreement
          with ChargeSuite and does not use it to train its models. No
          automated decisions with legal or similarly significant effects
          are made solely by the AI; all compliance outputs are advisory
          and require human review.
        </p>
      </Section>

      {/* 9. Cookies */}
      <Section id="section-cookies" title="9. Cookies and Tracking">
        <p>
          ChargeSuite uses only technically necessary cookies required for
          authentication (Clerk session tokens) and security (CSRF
          protection). We do not use advertising or tracking cookies.
          No cookie consent banner is displayed because we rely solely on
          strictly necessary cookies exempt from consent requirements
          under ePrivacy Directive Art. 5(3).
        </p>
      </Section>

      {/* 10. Changes */}
      <Section id="section-changes" title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material
          changes will be notified by e-mail to the registered operator
          contact at least 14 days before taking effect. The &ldquo;Last
          updated&rdquo; date at the top of this page always reflects the
          most recent revision.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
